import { buildPageSchema } from "@/seo/schema";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { SITE, SOCIAL } from "@/lib/site";
import { DEPLOY_REVISION } from "@/lib/deploy-revision";
import { MarkdownAlternateLink } from "@/components/MarkdownAlternateLink";
import { FloatingContact } from "@/components/FloatingContact";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-semibold text-foreground tracking-tight">404</h1>
        <h2 className="mt-4 text-xl font-medium text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-ink-soft">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Go home
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  if (import.meta.env.DEV) console.error(error);
  else console.error("[app error]", error.message);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-foreground">This page didn't load</h1>
        <p className="mt-2 text-sm text-ink-soft">Something went wrong on our end.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
          >
            Try again
          </button>
          <a href="/" className="rounded-full border border-border px-5 py-2.5 text-sm font-medium">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const SITE_DESCRIPTION =
  "Arif Khan — 23+ years designing electronic security, auditorium AV, home automation & luxury cinema solutions across India.";

// Sitewide entity graph — same nodes/IDs as every per-route graph
// (Person /#person → worksFor → Altima Global on altimaglobal.com).
const SITEWIDE_GRAPH = buildPageSchema();
const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
const GTM_ID = import.meta.env.VITE_GTM_CONTAINER_ID as string | undefined;
// Clarity Project ID is a public identifier (visible in client JS), safe to hardcode.
const CLARITY_ID = (import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined) ?? "wzw8mdox82";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => {
    const analyticsScripts: Array<{ src?: string; async?: boolean; children?: string }> = [];
    // Guard: never run analytics on /admin/* routes — admin pages handle
    // sensitive operator credentials and must not share an origin with
    // third-party scripts (Clarity/GTM/GA4) that could read sessionStorage
    // following a supply-chain compromise.
    const ADMIN_GUARD = `if(typeof location!=='undefined'&&(location.pathname==='/admin'||location.pathname.indexOf('/admin/')===0))return;`;
    if (GA4_ID) {
      // Load GA4 lazily inside an IIFE so we can apply the admin guard.
      analyticsScripts.push({
        children: `(function(){${ADMIN_GUARD}var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=${GA4_ID}';document.head.appendChild(s);window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${GA4_ID}',{anonymize_ip:true});})();`,
      });
    }
    // Defer GTM and Clarity until after the page is interactive — they add ~150KB
    // of blocking JS that hurts LCP/INP. requestIdleCallback (with setTimeout fallback)
    // schedules them when the browser is idle.
    if (GTM_ID) {
      analyticsScripts.push({
        children: `(function(){${ADMIN_GUARD}function load(){var f=document.getElementsByTagName('script')[0],j=document.createElement('script'),dl='&l=dataLayer';window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id=${GTM_ID}'+dl;f.parentNode.insertBefore(j,f);}if('requestIdleCallback' in window){requestIdleCallback(load,{timeout:3000});}else{setTimeout(load,2500);}})();`,
      });
    }
    if (CLARITY_ID) {
      analyticsScripts.push({
        children: `(function(){${ADMIN_GUARD}function load(){var c=window,l=document,a="clarity",r="script",i="${CLARITY_ID}";c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};var t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;var y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);}if('requestIdleCallback' in window){requestIdleCallback(load,{timeout:4000});}else{setTimeout(load,3500);}})();`,
      });
    }
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { name: "google-site-verification", content: "CIlJivzAmRz6CIGRA0X1KEyQDGCjSFJPXAfz4YsdLIY" },
        { title: "Arif Khan — Security Infrastructure & Building Tech" },
        { name: "description", content: SITE_DESCRIPTION },

        { name: "author", content: SITE.name },
        { name: "x-deploy-revision", content: DEPLOY_REVISION },
        { property: "og:site_name", content: SITE.name },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_IN" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: "@arifkhan_altima" },
        { name: "twitter:creator", content: "@arifkhan_altima" },
        { name: "theme-color", content: "#1A6BB5" },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        // Brand "AK" favicon set on navy (#0F1E3D).
        { rel: "icon", href: "/favicon.ico", sizes: "any" },
        { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
        { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
        { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
        { rel: "manifest", href: "/site.webmanifest" },
        // Crawler auto-discovery for AI/LLM-friendly mirrors.
        { rel: "alternate", type: "text/plain", href: "/llms.txt", title: "LLM site index" },
        { rel: "alternate", type: "application/ld+json", href: "/authority.json", title: "Authority graph (Person, Expertise, Topic clusters)" },
        { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
        { rel: "sitemap", type: "application/xml", href: "/sitemap-md.xml", title: "Markdown mirrors" },
      ],
      // No sitewide JSON-LD here: every content route emits one graph via
      // pageSchemaScript() that already includes the shared entity nodes.
      scripts: [...analyticsScripts],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <MarkdownAlternateLink />
      <Outlet />
      <FloatingContact />
    </QueryClientProvider>
  );
}
